import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  TrendingUp,
  TrendingDown,
  Theaters,
  Group,
  LocalActivity
} from '@mui/icons-material';

/**
 * AdvancedMetrics Component
 * 
 * Displays 7 pre-calculated metrics from API for tracking efficiency and opportunity:
 * 1. Excess Awareness - Aware but low interest
 * 2. Outstanding Interest - High interest among not yet aware
 * 3. Aware Opening Weekend - Aware planning opening weekend
 * 4. Outstanding Opening Weekend - Not yet aware planning opening weekend
 * 5. Theater Among Aware - Aware with theater intent
 * 6. Outstanding Theater - Not yet aware with theater intent
 * 7. Intent Conversion - Aware with viewing intent
 * 
 * Each metric uses pre-calculated API percentages + demographic breakdowns
 */
const AdvancedMetrics = ({ data }) => {
  const [expandedMetric, setExpandedMetric] = useState(null);

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for advanced metrics
        </Typography>
      </Paper>
    );
  }

  // Calculate overall totals and weighted averages for pre-calculated percentages
  const totals = data.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    // Sum the raw counts for weighting
    totalExcessAwareness: acc.totalExcessAwareness + (record.TotalExcessAwareness || 0),
    totalOutstandingInterest: acc.totalOutstandingInterest + (record.TotalOutstandingInterest || 0),
    totalAwareOpeningWeekend: acc.totalAwareOpeningWeekend + (record.TotalAwareOpeningWeekend || 0),
    totalOutstandingOpeningWeekend: acc.totalOutstandingOpeningWeekend + (record.TotalOutstandingOpeningWeekend || 0),
    totalTheaterAmongAware: acc.totalTheaterAmongAware + (record.TotalTheaterAmongAware || 0),
    totalOutstandingTheater: acc.totalOutstandingTheater + (record.TotalOutstandingTheater || 0),
    totalIntentConversion: acc.totalIntentConversion + (record.TotalIntentConversion || 0),
    // Also keep base totals for percentage calculations
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalInterest: acc.totalInterest + (record.TotalInterest || 0)
  }), {
    totalResponses: 0,
    totalExcessAwareness: 0,
    totalOutstandingInterest: 0,
    totalAwareOpeningWeekend: 0,
    totalOutstandingOpeningWeekend: 0,
    totalTheaterAmongAware: 0,
    totalOutstandingTheater: 0,
    totalIntentConversion: 0,
    totalAware: 0,
    totalInterest: 0
  });

  // Define 7 metrics using pre-calculated API fields
  const metrics = [
    {
      id: 'excess-awareness',
      title: 'Excess Awareness',
      description: 'Aware but low interest - indicates potential inefficiency in marketing spend',
      color: '#f44336',
      icon: <TrendingDown />,
      apiField: 'PctExcessAwareness',
      countField: 'TotalExcessAwareness',
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const excess = totals.totalExcessAwareness || 0;
        return aware > 0 ? Math.round((excess / aware) * 100) : 0;
      },
      interpretation: (value) => {
        if (value < 20) return { text: 'Excellent - minimal excess', color: '#4caf50' };
        if (value < 40) return { text: 'Good - manageable excess', color: '#8bc34a' };
        if (value < 60) return { text: 'Fair - some inefficiency', color: '#ff9800' };
        return { text: 'High - significant excess', color: '#f44336' };
      }
    },
    {
      id: 'outstanding-interest',
      title: 'Outstanding Interest',
      description: 'High interest among not yet aware - reachable audience opportunity',
      color: '#ffc107',
      icon: <TrendingUp />,
      apiField: 'PctOutstandingInterest',
      countField: 'TotalOutstandingInterest',
      getValue: (totals) => {
        const interest = totals.totalInterest || 0;
        const outstanding = totals.totalOutstandingInterest || 0;
        return interest > 0 ? Math.round((outstanding / interest) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 40) return { text: 'High opportunity - expand reach', color: '#4caf50' };
        if (value > 25) return { text: 'Good opportunity', color: '#8bc34a' };
        if (value > 15) return { text: 'Moderate opportunity', color: '#ff9800' };
        return { text: 'Limited opportunity', color: '#757575' };
      }
    },
    {
      id: 'aware-opening-weekend',
      title: 'Aware Opening Weekend',
      description: 'Aware audience planning to attend opening weekend',
      color: '#9c27b0',
      icon: <LocalActivity />,
      apiField: 'PctAwareOpeningWeekend',
      countField: 'TotalAwareOpeningWeekend',
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const opening = totals.totalAwareOpeningWeekend || 0;
        return aware > 0 ? Math.round((opening / aware) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 50) return { text: 'Strong opening support', color: '#4caf50' };
        if (value > 35) return { text: 'Good opening potential', color: '#8bc34a' };
        if (value > 20) return { text: 'Moderate opening', color: '#ff9800' };
        return { text: 'Limited opening', color: '#f44336' };
      }
    },
    {
      id: 'outstanding-opening-weekend',
      title: 'Outstanding Opening Weekend',
      description: 'Not yet aware but planning opening weekend - expansion opportunity',
      color: '#e91e63',
      icon: <Group />,
      apiField: 'PctOutstandingOpeningWeekend',
      countField: 'TotalOutstandingOpeningWeekend',
      getValue: (totals) => {
        const responses = totals.totalResponses || 0;
        const outstanding = totals.totalOutstandingOpeningWeekend || 0;
        return responses > 0 ? Math.round((outstanding / responses) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 30) return { text: 'High untapped demand', color: '#4caf50' };
        if (value > 20) return { text: 'Good expansion potential', color: '#8bc34a' };
        if (value > 10) return { text: 'Moderate opportunity', color: '#ff9800' };
        return { text: 'Limited untapped', color: '#757575' };
      }
    },
    {
      id: 'theater-among-aware',
      title: 'Theater Among Aware',
      description: 'Aware audiences with theater intent - theatrical appeal indicator',
      color: '#2196f3',
      icon: <Theaters />,
      apiField: 'PctTheaterAmongAware',
      countField: 'TotalTheaterAmongAware',
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const theater = totals.totalTheaterAmongAware || 0;
        return aware > 0 ? Math.round((theater / aware) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 60) return { text: 'Strong theatrical appeal', color: '#4caf50' };
        if (value > 45) return { text: 'Good theatrical appeal', color: '#8bc34a' };
        if (value > 30) return { text: 'Moderate appeal', color: '#ff9800' };
        return { text: 'Limited appeal', color: '#f44336' };
      }
    },
    {
      id: 'outstanding-theater',
      title: 'Outstanding Theater',
      description: 'Not yet aware with theater intent - addressable theater audience',
      color: '#00bcd4',
      icon: <Theaters />,
      apiField: 'PctOutstandingTheater',
      countField: 'TotalOutstandingTheater',
      getValue: (totals) => {
        const responses = totals.totalResponses || 0;
        const outstanding = totals.totalOutstandingTheater || 0;
        return responses > 0 ? Math.round((outstanding / responses) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 25) return { text: 'High theater potential', color: '#4caf50' };
        if (value > 15) return { text: 'Good potential', color: '#8bc34a' };
        if (value > 8) return { text: 'Moderate potential', color: '#ff9800' };
        return { text: 'Limited potential', color: '#757575' };
      }
    },
    {
      id: 'intent-conversion',
      title: 'Intent Conversion',
      description: 'Aware with viewing intent - activation rate from awareness',
      color: '#388e3c',
      icon: <TrendingUp />,
      apiField: 'PctIntentConversion',
      countField: 'TotalIntentConversion',
      getValue: (totals) => {
        const aware = totals.totalAware || 0;
        const intent = totals.totalIntentConversion || 0;
        return aware > 0 ? Math.round((intent / aware) * 100) : 0;
      },
      interpretation: (value) => {
        if (value > 70) return { text: 'Highly activated', color: '#4caf50' };
        if (value > 55) return { text: 'Well activated', color: '#8bc34a' };
        if (value > 40) return { text: 'Moderately activated', color: '#ff9800' };
        return { text: 'Low activation', color: '#f44336' };
      }
    }
  ];

  // Calculate overall values using getValue functions
  const overallValues = metrics.map(metric => ({
    ...metric,
    value: metric.getValue(totals),
    interpretation: metric.interpretation(metric.getValue(totals))
  }));

  // Calculate demographic breakdowns using pre-calculated API percentages
  const calculateDemographicBreakdowns = (metricDef) => {
    const breakdowns = {
      byEthnicity: {},
      byAge: {},
      byGender: {}
    };

    // Group by ethnicity - aggregate the API counts
    data.forEach(record => {
      const ethnicity = record.ethnicity || 'Unknown';
      if (!breakdowns.byEthnicity[ethnicity]) {
        breakdowns.byEthnicity[ethnicity] = {
          totalResponses: 0,
          totalAware: 0,
          totalInterest: 0,
          totalExcessAwareness: 0,
          totalOutstandingInterest: 0,
          totalAwareOpeningWeekend: 0,
          totalOutstandingOpeningWeekend: 0,
          totalTheaterAmongAware: 0,
          totalOutstandingTheater: 0,
          totalIntentConversion: 0
        };
      }
      breakdowns.byEthnicity[ethnicity].totalResponses += record.TotalResponses || 0;
      breakdowns.byEthnicity[ethnicity].totalAware += record.TotalAware || 0;
      breakdowns.byEthnicity[ethnicity].totalInterest += record.TotalInterest || 0;
      breakdowns.byEthnicity[ethnicity].totalExcessAwareness += record.TotalExcessAwareness || 0;
      breakdowns.byEthnicity[ethnicity].totalOutstandingInterest += record.TotalOutstandingInterest || 0;
      breakdowns.byEthnicity[ethnicity].totalAwareOpeningWeekend += record.TotalAwareOpeningWeekend || 0;
      breakdowns.byEthnicity[ethnicity].totalOutstandingOpeningWeekend += record.TotalOutstandingOpeningWeekend || 0;
      breakdowns.byEthnicity[ethnicity].totalTheaterAmongAware += record.TotalTheaterAmongAware || 0;
      breakdowns.byEthnicity[ethnicity].totalOutstandingTheater += record.TotalOutstandingTheater || 0;
      breakdowns.byEthnicity[ethnicity].totalIntentConversion += record.TotalIntentConversion || 0;
    });

    // Group by age
    data.forEach(record => {
      const age = record.agegroup || 'Unknown';
      if (!breakdowns.byAge[age]) {
        breakdowns.byAge[age] = {
          totalResponses: 0,
          totalAware: 0,
          totalInterest: 0,
          totalExcessAwareness: 0,
          totalOutstandingInterest: 0,
          totalAwareOpeningWeekend: 0,
          totalOutstandingOpeningWeekend: 0,
          totalTheaterAmongAware: 0,
          totalOutstandingTheater: 0,
          totalIntentConversion: 0
        };
      }
      breakdowns.byAge[age].totalResponses += record.TotalResponses || 0;
      breakdowns.byAge[age].totalAware += record.TotalAware || 0;
      breakdowns.byAge[age].totalInterest += record.TotalInterest || 0;
      breakdowns.byAge[age].totalExcessAwareness += record.TotalExcessAwareness || 0;
      breakdowns.byAge[age].totalOutstandingInterest += record.TotalOutstandingInterest || 0;
      breakdowns.byAge[age].totalAwareOpeningWeekend += record.TotalAwareOpeningWeekend || 0;
      breakdowns.byAge[age].totalOutstandingOpeningWeekend += record.TotalOutstandingOpeningWeekend || 0;
      breakdowns.byAge[age].totalTheaterAmongAware += record.TotalTheaterAmongAware || 0;
      breakdowns.byAge[age].totalOutstandingTheater += record.TotalOutstandingTheater || 0;
      breakdowns.byAge[age].totalIntentConversion += record.TotalIntentConversion || 0;
    });

    // Group by gender
    data.forEach(record => {
      const gender = record.gender || 'Unknown';
      if (!breakdowns.byGender[gender]) {
        breakdowns.byGender[gender] = {
          totalResponses: 0,
          totalAware: 0,
          totalInterest: 0,
          totalExcessAwareness: 0,
          totalOutstandingInterest: 0,
          totalAwareOpeningWeekend: 0,
          totalOutstandingOpeningWeekend: 0,
          totalTheaterAmongAware: 0,
          totalOutstandingTheater: 0,
          totalIntentConversion: 0
        };
      }
      breakdowns.byGender[gender].totalResponses += record.TotalResponses || 0;
      breakdowns.byGender[gender].totalAware += record.TotalAware || 0;
      breakdowns.byGender[gender].totalInterest += record.TotalInterest || 0;
      breakdowns.byGender[gender].totalExcessAwareness += record.TotalExcessAwareness || 0;
      breakdowns.byGender[gender].totalOutstandingInterest += record.TotalOutstandingInterest || 0;
      breakdowns.byGender[gender].totalAwareOpeningWeekend += record.TotalAwareOpeningWeekend || 0;
      breakdowns.byGender[gender].totalOutstandingOpeningWeekend += record.TotalOutstandingOpeningWeekend || 0;
      breakdowns.byGender[gender].totalTheaterAmongAware += record.TotalTheaterAmongAware || 0;
      breakdowns.byGender[gender].totalOutstandingTheater += record.TotalOutstandingTheater || 0;
      breakdowns.byGender[gender].totalIntentConversion += record.TotalIntentConversion || 0;
    });

    // Calculate values for each breakdown using getValue function
    return {
      byEthnicity: Object.entries(breakdowns.byEthnicity).map(([name, groupData]) => ({
        name,
        value: metricDef.getValue(groupData)
      })).sort((a, b) => b.value - a.value),
      byAge: Object.entries(breakdowns.byAge).map(([name, groupData]) => ({
        name,
        value: metricDef.getValue(groupData)
      })).sort((a, b) => b.value - a.value),
      byGender: Object.entries(breakdowns.byGender).map(([name, groupData]) => ({
        name,
        value: metricDef.getValue(groupData)
      })).sort((a, b) => b.value - a.value)
    };
  };

  const handleToggleExpand = (metricId) => {
    setExpandedMetric(expandedMetric === metricId ? null : metricId);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Advanced Metrics for Tracking
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Additional metrics to drive efficient marketing spend and targeted promo patterns
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {overallValues.map((metric, index) => {
          const isExpanded = expandedMetric === metric.id;
          const breakdowns = isExpanded ? calculateDemographicBreakdowns(metrics[index]) : null;

          return (
            <Grid item xs={12} md={6} lg={4} key={metric.id}>
              <Card
                sx={{
                  border: `2px solid ${metric.color}30`,
                  backgroundColor: metric.color + '05'
                }}
              >
                <CardContent>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: metric.color, mb: 0.5 }}>
                        {metric.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {metric.description}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      {metric.icon && React.cloneElement(metric.icon, { sx: { color: metric.color, fontSize: 28 } })}
                    </Box>
                  </Box>

                  {/* Overall Value */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: metric.color }}>
                      {metric.value}%
                    </Typography>
                  </Box>

                  {/* Interpretation */}
                  <Chip
                    label={metric.interpretation.text}
                    size="small"
                    sx={{
                      backgroundColor: metric.interpretation.color + '20',
                      color: metric.interpretation.color,
                      fontWeight: 600,
                      mb: 2
                    }}
                  />

                  {/* Expand for Demographic Breakdown */}
                  <Box
                    onClick={() => handleToggleExpand(metric.id)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      View Demographic Breakdown
                    </Typography>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  {/* Demographic Breakdown Tables */}
                  <Collapse in={isExpanded}>
                    {breakdowns && (
                      <Box sx={{ mt: 2 }}>
                        {/* By Ethnicity */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                            By Ethnicity
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Ethnicity</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {breakdowns.byEthnicity.map((item) => (
                                  <TableRow key={item.name} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{item.name}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                      {item.value}%
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        {/* By Age */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                            By Age Group
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Age Group</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {breakdowns.byAge.map((item) => (
                                  <TableRow key={item.name} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{item.name}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                      {item.value}%
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        {/* By Gender */}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                            By Gender
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Gender</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {breakdowns.byGender.map((item) => (
                                  <TableRow key={item.name} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{item.name}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                      {item.value}%
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default AdvancedMetrics;

